import createGraphqlClient, {
  type ClientOptions,
} from '@seek/ca-graphql-client';
import type {
  browserHttpLink,
  nodeHttpLink,
} from '@seek/ca-graphql-client/src/http';
import { onError } from '@apollo/client/link/error';
import { metrics } from '@seek/metrics-js';
import type { Country } from '@seek/chalice-types';

import logger from './modules/logger';

import mockOverrides from 'src/mock-overrides';
import resolverOverrides from 'src/resolver-overrides';
import type { Brand } from '@seek/audience-zones';
export interface Client {
  environment: string;
  httpLink: typeof browserHttpLink | typeof nodeHttpLink;
  brand: Brand;
  country: Country;
  graphqlEndpoint?: string;
}

export const redirectToSignIn = () => {
  if (window) {
    const returnUrl = window.location.pathname;
    window.location.href = `/oauth/login/?returnUrl=${returnUrl}`;
  }
};

const linkError = onError(({ graphQLErrors, networkError }) => {
  metrics.count('graphqlError');
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const errorText = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
      logger(errorText);
      if (extensions?.code === 'UNAUTHENTICATED') {
        redirectToSignIn();
      }
    });
  }

  if (networkError) {
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      redirectToSignIn();
    }
    logger(`[Network error]: ${networkError}`);
  }
});

const getClient = ({
  environment,
  httpLink,
  brand,
  country,
  graphqlEndpoint,
}: Client) => {
  const links = [linkError];

  const gqlClientConfigs: Record<
    typeof environment,
    Pick<
      ClientOptions,
      'mock' | 'authOptions' | 'mockOverrides' | 'resolverOverrides'
    >
  > = {
    production: {
      mock: country === 'HK',
      authOptions: {
        useAuth: true,
        forceStrategy: 'AUTH0',
      },
    },
    staging: {
      authOptions: {
        useAuth: true,
        forceStrategy: 'AUTH0',
      },
    },
    development: {
      ...(graphqlEndpoint ? { graphqlEndpointOverride: graphqlEndpoint } : {}),
      authOptions: {
        useAuth: false,
      },
      mock: true,
      mockOverrides: mockOverrides(),
      resolverOverrides: resolverOverrides(),
    },
  };
  return createGraphqlClient({
    links,
    headers: {
      'x-seek-site': 'SEEK Web Recommended Jobs',
    },
    appName: 'disc-static-pages',
    httpLink,
    ...(gqlClientConfigs[environment] || gqlClientConfigs.production),
    brand: brand as ClientOptions['brand'],
    country: country as ClientOptions['country'],
  });
};

export default getClient;
